import { MapFilterState } from '@ivy-monorepo/shared'
import Icon from '../../Icon'
import { MapFilterConfigItem, MapFilters } from '../../../types/liveMap'
import { fieldForKey, isLocked, visibleFilters } from '../../../utils/liveMap/filterConfig'

/**
 * Floating filter card (top-left) driven by the server `mapFilters` config.
 * - "All" header row with a close (X) button.
 * - One row per non-hidden filter: colored ring + label.
 * - Locked rows show a lock icon and are non-interactive (always active).
 * - Toggleable rows show a checkmark when active, an empty ring when inactive.
 * - offUntilEnabled (Default) rows render greyed-out while off.
 */

interface MapFilterCardProps {
  config: MapFilterConfigItem[]
  filters: MapFilters
  onToggle: (field: keyof MapFilters) => void
  onClose: () => void
}

const MapFilterCard = ({ config, filters, onToggle, onClose }: MapFilterCardProps) => {
  const rows = visibleFilters(config)

  return (
    <div className="map-filter-card">
      <div className="map-filter-card__header">
        <span className="map-filter-card__title">All</span>
        <button
          type="button"
          className="map-filter-card__close"
          aria-label="Close filters"
          onClick={onClose}
        >
          <Icon name="Xmark" />
        </button>
      </div>

      <ul className="map-filter-card__list">
        {rows.map(item => {
          const field = fieldForKey(item.key)
          const locked = isLocked(item)
          const active = locked || (field ? filters[field] : false)
          const dimmed = item.state === MapFilterState.Default && !active

          return (
            <li
              key={item.key}
              className={
                'map-filter-row' +
                (active ? ' map-filter-row--active' : '') +
                (dimmed ? ' map-filter-row--dimmed' : '') +
                (locked ? ' map-filter-row--locked' : '')
              }
              role={locked ? undefined : 'button'}
              tabIndex={locked ? undefined : 0}
              onClick={() => {
                if (!locked && field) onToggle(field)
              }}
              onKeyDown={e => {
                if (!locked && field && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault()
                  onToggle(field)
                }
              }}
            >
              <span
                className={'map-filter-row__ring' + (active ? ' map-filter-row__ring--filled' : '')}
                style={{ borderColor: item.color, backgroundColor: active ? item.color : 'transparent' }}
              >
                {active && !locked && <Icon name="Check" className="map-filter-row__check" />}
              </span>

              <span className="map-filter-row__label">{item.label}</span>

              {locked && <Icon name="Lock" className="map-filter-row__lock" />}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default MapFilterCard
